import React from "react";
import PollIntervalContext from "./PollIntervalContext";

/**
 * Subscribes to the nearest {@link PollIntervalProvider} and calls `callback`
 * on every interval tick.
 *
 * The latest version of `callback` is always used without re-subscribing —
 * the subscription is established once on mount and torn down on unmount.
 *
 * @example
 * ```ts
 * const { data, refetch } = useQuery(MY_QUERY, { skip: !enabled });
 * usePollInterval(() => { if (enabled) refetch(); });
 * ```
 */
export const usePollInterval = (callback: () => void): void => {
  const { subscribe } = React.useContext(PollIntervalContext);

  // Keep a stable ref so the subscription never needs to re-register when
  // the callback identity changes across renders (e.g. inline arrow functions).
  const callbackRef = React.useRef(callback);
  callbackRef.current = callback;

  React.useEffect(() => {
    return subscribe(() => callbackRef.current());
    // subscribe is stable (useCallback with []), so this only runs once.
  }, [subscribe]);
};
